import { useState } from 'react'
import { LogOut } from 'lucide-react'
import { Link } from 'react-router'
import { Icon } from '../platform/icons/Icon'
import { Button } from '../platform/primitives/Button'
import { useHitTarget } from '../platform/responsive/hitTarget'
import { useBreakpoint } from '../platform/responsive/useBreakpoint'
import { useAuth } from './useAuth'
import { useProfileValue } from './useProfile'
import { initialsFor } from '../platform/primitives/PersonAvatar'

/**
 * The account identity and the way out.
 *
 * **The app had no sign-out at all.** `signOut` landed on the provider with no
 * caller outside the session-unavailable screen, and the top bar defers its
 * account menu to a later issue — correct while there was no session to end,
 * and not correct once the guard exists. Without this a tester cannot sign in
 * twice, so the module is unverifiable rather than merely incomplete.
 *
 * **Not the full account menu.** That surface wants notifications, profile and
 * preferences — three destinations that do not exist — and a dropdown holding
 * one item is a worse shape than a button. This is deliberately the smallest
 * thing that closes the gap, and it gets replaced wholesale.
 *
 * **No confirmation.** CLAUDE.md scopes the confirm-and-state-what-is-lost rule
 * to destructive actions. Signing out loses nothing and costs one click to
 * undo; a dialog here would also be the app's first overlay.
 *
 * The email is shown because a browser is a shared-machine context in a way a
 * phone is not — "Sign out" alone does not say whose session ends.
 */
export function AccountMenu() {
  const auth = useAuth()
  const breakpoint = useBreakpoint()
  const avatarSize = useHitTarget('page')
  const [busy, setBusy] = useState(false)
  const profileName = useProfileValue()?.display_name?.trim()

  // `signedIn` only. `bootstrapping` paints the shell for a frame, and a
  // sign-out fired against a half-built session races the provider's own
  // transition.
  if (auth.status !== 'signedIn') return null

  /**
   * The address is dropped on a phone, and the control never is.
   *
   * At 320px the bar is already carrying a breadcrumb and the appearance
   * toggle, and an email is 16–60 characters that cannot be abbreviated
   * meaningfully. Keeping it there did not degrade gracefully — it wrapped to
   * an 11px column 195px tall, took the bar to 224px, and still overflowed the
   * document by 4px, which is M0-R18. web-tester measured it.
   *
   * Eliding instead was the other option and is worse: the string's whole job
   * is telling you WHICH account this is, and `sam@…` on a shared machine
   * answers that no better than nothing does. The shared-machine case this
   * exists for is a desktop one; a phone is a single-user device.
   *
   * `useBreakpoint()`, not a media query — CLAUDE.md's single source, and that
   * is the whole reason. An earlier version of this comment added that
   * `display: none` "would leave the text in the accessibility tree and in the
   * min-content contribution": both halves are false. `display: none` generates
   * no box and is removed from the tree. Those claims are true of a
   * visually-hidden technique — `clip`, `sr-only` — and were mislabelled here.
   * The conclusion stands on the rule alone; the supporting premise did not.
   */
  const showEmail = breakpoint !== 'phone' && auth.user.email !== null

  /** #225. The same breakpoint the email uses, named separately because the two
   *  are different decisions about the same bar and would not have to move
   *  together. */
  const compact = breakpoint === 'phone'
  const label = busy ? 'Signing out…' : 'Sign out'

  /**
   * The avatar's initials — #205.
   *
   * The design puts a lime circle with the user's initials in the top-right,
   * where this used to render the email as plain text beside a full-size
   * "Sign out" button.
   *
   * Derived from the email's local part, and ONLY from it: `AuthUser` is
   * `{ uid, email, emailVerified }` and carries no display name. The design's
   * "SC" comes from a full name we do not have here — reading one would mean
   * either widening the port or fetching `GET /users/me`, and inventing a
   * field to make the avatar look right is how a component ends up rendering
   * `undefined`. Recorded on #205 rather than papered over.
   *
   * `·` when there is no email at all, which a federated identity can produce
   * — the block below already guards for exactly that. A neutral mark beats an
   * empty circle, and neither is ever the ACCESSIBLE name: that comes from the
   * label on the control, so a screen-reader user is never told "S".
   */
  const emailInitials =
    (auth.user.email?.split('@')[0] ?? '')
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]!.toUpperCase())
      .join('') || '·'

  // The display name wins once the profile query has delivered one; until then
  // (or with no name set) the email-derived initials above stand. The label and
  // title stay email-based: they say WHICH account, and a name does not.
  const initials = profileName ? initialsFor(profileName, null) : emailInitials

  return (
    <div className="topbar__account">
      {/* Conditional on its own, not wrapped around the button: a federated
          identity can arrive with no email, and the nicety must not take the
          essential control with it when it is missing. */}
      {showEmail && <span className="topbar__account-email">{auth.user.email}</span>}

      {/**
       * The avatar — #205.
       *
       * A link, not a decoration: the design's avatar is the entry to Settings
       * and this is the only control in the top bar that goes there now that
       * Settings has left the sidebar's group list. `title` gives a pointer
       * user the email, which the initials cannot carry.
       *
       * Lime is permitted here under #197's settled rule: persistent chrome
       * sits outside the one-fill-per-content-area count.
       */}
      <Link
        to="/settings/profile"
        className="topbar__avatar hoverable"
        aria-label={auth.user.email ? `Settings for ${auth.user.email}` : 'Settings'}
        title={auth.user.email ?? 'Settings'}
        // #90. Both axes, inline: it is a circle, and one that grew only in
        // height would be an ellipse still missing the rule on the other axis.
        // shell.css cannot do this — a media query there is a second
        // breakpoint source, which is the whole reason #90 exists.
        style={{ width: avatarSize, height: avatarSize }}
      >
        <span aria-hidden="true">{initials}</span>
      </Link>
      {/**
       * Icon-only on a phone — #225.
       *
       * With the rail gone (#109) every chrome route still scrolled sideways
       * 17–40px at 320px, and the sweep blamed a 58px-wide
       * `button.btn--outline.btn--size-page`: this control, its label plus
       * `btn--size-page`'s `0 14px` padding. The label is what does not fit.
       *
       * **Not a hit-target reduction.** The box is `useHitTarget('page')` and
       * is 44px on a phone either way — what goes is the text run beside the
       * glyph, exactly as `TopBar` already does for the appearance toggle and
       * as this component already does for the email. #225 explicitly rules
       * out buying the pixels back from M0-R29, and this does not.
       *
       * The name moves to `aria-label` rather than being lost, and it carries
       * the in-flight state too — icon-only, that string is the only feedback
       * between the tap and the redirect.
       */}
      <Button
        variant="outline"
        size="page"
        disabled={busy}
        aria-label={compact ? label : undefined}
        // A pointer user on a narrow window gets no tooltip from an icon
        // otherwise — the avatar beside it already carries one. The accessible
        // name is on `aria-label`; this is for the mouse.
        title={compact ? label : undefined}
        onClick={() => {
          setBusy(true)
          // No `finally` resetting `busy`: the provider's teardown runs even when the
          // port rejects, so the session ends locally and the guard unmounts this
          // component either way. The catch only keeps a port failure from becoming
          // an unhandled rejection; `setBusy(false)` covers the unmount-less case.
          void auth.signOut().catch(() => setBusy(false))
        }}
      >
        {compact ? <Icon glyph={LogOut} /> : label}
      </Button>
    </div>
  )
}
